import { useState, useCallback, useRef, useEffect } from 'react';
import { apiRequest } from '../utils/api';

/**
 * Custom hook for silent historical data polling during onboarding
 * Handles background historical data collection without showing progress to user
 */
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const useHistoricalDataPolling = () => {
  const [isPolling, setIsPolling] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const consecutiveErrorsRef = useRef(0);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Start silent polling for historical data task
  const startSilentPolling = useCallback((historicalTaskId) => {
    if (!historicalTaskId) {
      console.warn('🎯 [HistoricalPolling] No task ID provided');
      return;
    }

    console.log('🎯 [HistoricalPolling] Starting silent polling for historical data:', historicalTaskId);
    
    setTaskId(historicalTaskId);
    setIsPolling(true);
    setIsComplete(false);
    setError(null);
    consecutiveErrorsRef.current = 0;

    timeoutRef.current = setTimeout(() => {
      console.log('🎯 [HistoricalPolling] Timeout reached (60s) - stopping polling');
      cleanup();
      setIsPolling(false);
      setError('timeout');
    }, 60000);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await apiRequest(`/api/analyze/status/${historicalTaskId}/`);
        
        if (!response.ok) {
          throw new Error(`Status check failed: ${response.status}`);
        }

        consecutiveErrorsRef.current = 0;
        const data = await response.json();
        console.log('🎯 [HistoricalPolling] Status check:', data.status);

        if (data.status === 'completed') {
          console.log('🎯 [HistoricalPolling] Historical data collection complete!');
          cleanup();
          setIsPolling(false);
          setIsComplete(true);
        } else if (data.status === 'failed' || data.status === 'error') {
          console.error('🎯 [HistoricalPolling] Historical data collection failed:', data.error);
          cleanup();
          setIsPolling(false);
          setError(data.error || 'Historical data collection failed');
        }
        
      } catch (err) {
        consecutiveErrorsRef.current += 1;
        console.warn(`[HistoricalPolling] Poll error (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_POLL_ERRORS}):`, err.message);
        
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
          cleanup();
          setIsPolling(false);
          setError(err.message);
        }
      }
    }, 3000);

  }, [cleanup]);

  // Stop polling
  const stopPolling = useCallback(() => {
    console.log('🎯 [HistoricalPolling] Stopping polling');
    cleanup();
    setIsPolling(false);
  }, [cleanup]);

  // Reset state
  const reset = useCallback(() => {
    cleanup();
    setIsPolling(false);
    setIsComplete(false);
    setTaskId(null);
    setError(null);
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    // State
    isPolling,
    isComplete,
    error,
    taskId,
    
    // Actions
    startSilentPolling,
    stopPolling,
    reset
  };
};

export default useHistoricalDataPolling;

